import { useId, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { shipmentsApi } from '../../api/shipments-api';
import { ConflictError } from '../../api/errors';
import { PartialFailureNotice } from '../../components/PartialFailureNotice';
import type { Shipment } from '../../domain/shipment';
import { bookingErrorMessage, isUnknownOutcome } from '../shipment-create/booking-errors';

/**
 * Hands a pending shipment booked through koi to its carrier in Cargonizer. Cargonizer doesn't
 * allow the consignment to be changed afterwards, so it asks first.
 */
export function TransferToCarrierAction({ shipment }: { shipment: Shipment }) {
  const queryClient = useQueryClient();
  const dialogId = useId();
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [unknownOutcome, setUnknownOutcome] = useState(false);

  const refreshShipment = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ['shipment', shipment.id] }),
      queryClient.invalidateQueries({ queryKey: ['shipment-evidence', shipment.id] }),
      queryClient.invalidateQueries({ queryKey: ['shipments'] }),
    ]);

  const transfer = useMutation({
    mutationFn: () => shipmentsApi.transferShipmentToCarrier(shipment.id, shipment.version),
    onSuccess: async () => {
      await refreshShipment();
    },
    onError: (err: unknown) => {
      if (isUnknownOutcome(err)) {
        setUnknownOutcome(true);
        return;
      }
      setError(
        err instanceof ConflictError
          ? 'This shipment changed after you opened it. Reload it and check before transferring.'
          : bookingErrorMessage(err),
      );
    },
  });

  if (shipment.state !== 'PENDING' || !shipment.cargonizerConsignmentId) return null;

  return (
    <div className="transfer-to-carrier">
      <button
        type="button"
        onClick={() => {
          setError(null);
          setUnknownOutcome(false);
          setConfirming(true);
        }}
        disabled={transfer.isPending}
      >
        {transfer.isPending ? 'Transferring…' : 'Transfer to carrier'}
      </button>
      {error ? (
        <p role="alert" className="form-error">
          {error}
        </p>
      ) : null}
      {unknownOutcome ? (
        <PartialFailureNotice
          onCheckCurrentState={() => {
            setUnknownOutcome(false);
            void refreshShipment();
          }}
        />
      ) : null}
      {confirming ? (
        <div
          className="drawer-overlay centered-overlay"
          role="presentation"
          onClick={() => setConfirming(false)}
        >
          <div
            className="confirmation-dialog"
            role="alertdialog"
            aria-labelledby={`${dialogId}-title`}
            aria-describedby={`${dialogId}-body`}
            onClick={(event) => event.stopPropagation()}
          >
            <div className="drawer-heading">
              <h2 id={`${dialogId}-title`}>Transfer to {shipment.carrier.split(' — ')[0]}?</h2>
            </div>
            <div className="warning-callout" id={`${dialogId}-body`}>
              <p>
                This sends consignment {shipment.cargonizerConsignmentNumber} to the carrier. After
                that, it can’t be changed in Cargonizer.{' '}
                {shipment.direction === 'RETURN'
                  ? 'Transfer once the customer has the label and the goods are ready.'
                  : 'Transfer once the goods are packed and labelled.'}
              </p>
            </div>
            <div className="dialog-actions">
              <button type="button" autoFocus onClick={() => setConfirming(false)}>
                Cancel
              </button>
              <button
                type="button"
                className="primary"
                onClick={() => {
                  setConfirming(false);
                  transfer.mutate();
                }}
              >
                Transfer to carrier
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
